import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import {
  Send,
  Check,
  X,
  Edit2,
  AlertCircle,
  Bot,
  ChevronDown,
  ChevronRight,
  Phone,
  RefreshCw,
  Forward,
  Mail,
  MailX,
  Paperclip,
  Sparkles,
  UserRound,
} from 'lucide-react'
import { cn, formatStatus } from '@/lib/utils'
import { SenderAvatar } from '@/features/communications/message-row'
import { formatMessageTime } from '@/features/communications/message-format'
import { stripQuotedHtml, stripQuotedText } from '@/lib/email-body'
import type { CommunicationMessage, MessageAnalysis, MessageDraftUpdate } from '@/features/communications/types'
import { EmailRecipientsField } from '@/components/email-recipients-field'
import { useEmailRecipients } from '@/components/use-email-recipients'
import { MessageAttachments, MessageEmailBody } from '@/features/communications/attachments'
import { ForwardMessageDialog } from '@/features/communications/forward-message'
import { AnalysisStatusLine, AnalysisSweep } from '@/features/communications/analysis-status'
import {
  deliveryReport,
  highlight,
  messagePreview,
  parseEmailList,
} from '@/features/communications/thread-display'
import { useThreadMailbox } from '@/features/communications/mailbox'
import {
  MessageActionItems,
  type MessageActionLink,
} from '@/features/communications/message-actions'
import { ComposeTextarea } from '@/components/compose-field'
import { CallMessageBody } from '@/features/phone-calls/call-details'

/** Statuses worth calling out; sent and received are the ordinary state of an email. */
const NOTABLE_STATUSES: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  draft: 'outline',
  pending_approval: 'default',
  sending: 'secondary',
  invalidated: 'outline',
  failed: 'destructive',
}

function aiCategoryBadge(category: string) {
  const colors: Record<string, string> = {
    acknowledged: 'bg-blue-100 text-blue-800',
    confirmed: 'bg-green-100 text-green-800',
    shipped: 'bg-green-100 text-green-800',
    tracking_update: 'bg-green-100 text-green-800',
    delayed: 'bg-yellow-100 text-yellow-800',
    backorder: 'bg-yellow-100 text-yellow-800',
    price_change: 'bg-orange-100 text-orange-800',
    question: 'bg-purple-100 text-purple-800',
    cancellation: 'bg-red-100 text-red-800',
    requires_action: 'bg-red-100 text-red-800',
  }
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${colors[category] || 'bg-gray-100 text-gray-800'}`}
    >
      {formatStatus(category)}
    </span>
  )
}

const UNSENT_STATUSES = new Set(['draft', 'pending_approval', 'invalidated'])
const REPLY_PREFIX = /^((re|fw|fwd)\s*:\s*)+/i

function sameSubject(a: string, b: string) {
  return a.replace(REPLY_PREFIX, '').trim().toLowerCase() === b.replace(REPLY_PREFIX, '').trim().toLowerCase()
}

type SenderKind = 'staff' | 'automation' | 'mailbox' | null

function sentByKind(message: CommunicationMessage): SenderKind {
  return UNSENT_STATUSES.has(message.status) ? null : (message.sent_by_kind ?? null)
}

/** Round mark beside each message: who it came from at a glance. */
function SenderMark({
  message,
  counterpartLabel,
}: {
  message: CommunicationMessage
  counterpartLabel: string
}) {
  if (message.message_type === 'note' || message.message_type === 'call')
    return <SenderAvatar tone="note" icon={Phone} />
  if (deliveryReport(message)) return <SenderAvatar tone="failed" icon={MailX} />
  if (message.direction === 'outbound') {
    const kind = sentByKind(message)
    return <SenderAvatar tone="ours" icon={kind === 'automation' ? Bot : kind === 'mailbox' ? Mail : UserRound} />
  }
  return <SenderAvatar tone="counterpart" name={message.from_name || message.from_email || counterpartLabel} />
}

/** Who sent an outbound email: a named staff member, our automation, or the Outlook mailbox. */
function OutboundSender({
  message,
  senderName,
}: {
  message: CommunicationMessage
  senderName?: string | null
}) {
  const kind = sentByKind(message)
  if (kind === 'staff') {
    return <span title="Sent from the dashboard">{senderName || 'Staff member'}</span>
  }
  if (kind === 'automation') {
    return <span title="Sent automatically by USA-RS">Automated</span>
  }
  if (kind === 'mailbox') {
    return (
      <span title={`Sent directly from ${message.from_email || 'the shared mailbox'}`}>Outlook</span>
    )
  }
  return <span>{message.from_name || 'USA-RS'}</span>
}

export function ThreadMessage({
  message,
  senderName,
  aiAnalysis,
  analysis,
  actionLinks = [],
  threadSubject,
  expanded = true,
  onToggle,
  query = '',
  onApprove,
  onReject,
  onSend,
  onUpdate,
  onRetry,
  isRetrying,
}: {
  message: CommunicationMessage
  /** Staff name for `message.approved_by`, resolved by the caller's staff directory. */
  senderName?: string | null
  /** The AI's written analysis of this message, stored as its own system message. */
  aiAnalysis?: CommunicationMessage
  /** Where this message's AI analysis stands when it has not finished. */
  analysis?: MessageAnalysis
  /** Action items this email raised or resolved. */
  actionLinks?: MessageActionLink[]
  threadSubject?: string
  expanded?: boolean
  onToggle?: () => void
  query?: string
  onApprove: (uuid: string) => void
  onReject: (uuid: string) => void
  onSend: (uuid: string) => void
  onUpdate: (uuid: string, body: MessageDraftUpdate) => void
  onRetry: (uuid: string) => void
  isRetrying: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(message.body_text || '')
  const [showAnalysis, setShowAnalysis] = useState(false)
  const [forwardOpen, setForwardOpen] = useState(false)
  // Customer threads (USA-1328) have no forwarding route; their senders are customers.
  const mailbox = useThreadMailbox()
  const counterpartLabel = mailbox === 'customer' ? 'Customer' : 'Manufacturer'
  const isOutbound = message.direction === 'outbound'
  // A Quo phone call (USA-217): its subject is the call line ("Incoming call · 4m 12s · Sam").
  const isCall = message.message_type === 'call'
  const isPending = message.status === 'pending_approval'
  const isDraft = message.status === 'draft'
  const isFailed = message.status === 'failed' && isOutbound
  const canForward =
    mailbox !== 'customer' &&
    message.message_type === 'email' &&
    !!message.graph_message_id &&
    (message.status === 'sent' || message.status === 'received')

  const cleanHtml = message.body_html ? stripQuotedHtml(message.body_html) : null
  const cleanText = message.body_text ? stripQuotedText(message.body_text) : null

  const toEmails = parseEmailList(message.to_emails)
  const ccEmails = parseEmailList(message.cc_emails)
  // Who a draft goes to changes until it reaches Outlook (USA-1663).
  const recipientsEditable = isOutbound && (isPending || isDraft) && !message.graph_message_id
  const draftRecipients = useEmailRecipients({ to: toEmails, cc: ccEmails })
  const report = deliveryReport(message)
  // A bounce's AI category ("Other") says nothing its one line does not.
  const category = report ? null : message.ai_category || aiAnalysis?.ai_category
  const statusVariant = NOTABLE_STATUSES[message.status]
  const preview = messagePreview(message, query)
  const time = message.sent_at || message.received_at || message.created_at
  const showSubject =
    !isCall && !!message.subject && (!threadSubject || !sameSubject(message.subject, threadSubject))

  const header = (
    <>
      <SenderMark message={message} counterpartLabel={counterpartLabel} />
      <span className="block min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className={cn('truncate text-sm font-semibold text-foreground', isCall && 'shrink-0 max-w-[45%]')}>
            {isOutbound ? (
              <OutboundSender message={message} senderName={senderName} />
            ) : report ? (
              <span title={`Report from ${message.from_email}`}>{report.label}</span>
            ) : (
              message.from_name || message.from_email || counterpartLabel
            )}
          </span>
          {message.message_type === 'note' && (
            <span className="shrink-0 text-xs font-medium text-amber-700 dark:text-amber-300">
              Recorded
            </span>
          )}
          {message.message_type === 'sms' && (
            // A Quo text in an email conversation (USA-217): it was texted, not emailed.
            <span className="shrink-0 text-xs font-medium text-amber-700 dark:text-amber-300">Text</span>
          )}
          {isCall && message.subject && (
            <span className="min-w-0 truncate text-xs font-medium text-amber-700 dark:text-amber-300">
              {message.subject}
            </span>
          )}
          {statusVariant && (
            <Badge variant={statusVariant} className="shrink-0 text-[11px]">
              {formatStatus(message.status)}
            </Badge>
          )}
          {category && <span className="hidden sm:contents">{aiCategoryBadge(category)}</span>}
          <span className="ml-auto flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
            {message.has_attachments && <Paperclip className="h-3 w-3" aria-label="Has attachments" />}
            {time && <time dateTime={time}>{formatMessageTime(time)}</time>}
          </span>
        </span>
        {report && report.recipients.length > 0 && (
          <span className="mt-0.5 block truncate text-sm text-muted-foreground">
            {report.recipients.join(', ')}
          </span>
        )}
        {!expanded && !report && preview.text && (
          <span className="mt-0.5 line-clamp-2 text-sm text-muted-foreground md:line-clamp-1">
            {preview.fromAi && (
              <Sparkles
                className="mr-1 inline h-3 w-3 -translate-y-px text-violet-500"
                aria-label="AI summary"
              />
            )}
            {highlight(preview.text, query)}
          </span>
        )}
        {expanded && !report && !recipientsEditable && (toEmails.length > 0 || ccEmails.length > 0) && (
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {toEmails.length > 0 && <>to {toEmails.join(', ')}</>}
            {ccEmails.length > 0 && <> · cc {ccEmails.join(', ')}</>}
          </span>
        )}
      </span>
    </>
  )

  return (
    <article
      data-testid={`communication-message-${message.uuid}`}
      data-expanded={expanded}
      className="relative"
    >
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none sm:px-4"
        >
          {header}
        </button>
      ) : (
        <div className="flex items-start gap-3 px-3 py-2.5 sm:px-4">{header}</div>
      )}
      {analysis && (
        <AnalysisStatusLine
          analysis={analysis}
          className={cn('-mt-1 pl-14 pr-3 sm:pl-[3.75rem]', expanded ? 'pb-2' : 'pb-2.5')}
        />
      )}

      {!expanded && actionLinks.some((link) => link.closed === null) && (
        <div className="pb-2.5 pl-14 pr-3 sm:pl-[3.75rem] sm:pr-4">
          <MessageActionItems links={actionLinks} compact />
        </div>
      )}

      {expanded && (
        <div className="min-w-0 space-y-2 pb-3 pl-14 pr-3 sm:pl-[3.75rem] sm:pr-4">
          {category && <div className="sm:hidden">{aiCategoryBadge(category)}</div>}
          {showSubject && (
            <p className="text-xs font-medium text-muted-foreground">{message.subject}</p>
          )}

          {recipientsEditable && !editing && (
            <EmailRecipientsField
              state={draftRecipients}
              fallback="The conversation's contact"
              onChange={(next) => onUpdate(message.uuid!, { to_emails: next.to, cc_emails: next.cc })}
            />
          )}

          {editing ? (
            <div className="space-y-2">
              <ComposeTextarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={4}
                className="text-sm"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    onUpdate(message.uuid!, { body_text: editText })
                    setEditing(false)
                  }}
                >
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : isCall ? (
            <CallMessageBody
              messageUuid={message.uuid ?? undefined}
              fallbackText={cleanText}
              theirName={message.from_name}
            />
          ) : cleanHtml ? (
            <MessageEmailBody html={cleanHtml} messageUuid={message.uuid ?? undefined} showEmbeddedImages />
          ) : (
            <div className="whitespace-pre-wrap text-sm">{cleanText}</div>
          )}

          <MessageAttachments
            messageUuid={message.uuid ?? undefined}
            hasAttachments={message.has_attachments}
          />

          {isFailed && (
            <div className="space-y-2 overflow-hidden rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs">
              <div className="flex items-start gap-1.5 text-destructive">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div className="min-w-0 flex-1 break-words">
                  <p className="font-medium">Failed to send</p>
                  {message.error_message && (
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-destructive/80">
                      {message.error_message}
                    </p>
                  )}
                </div>
              </div>
              <LoadingButton
                size="sm"
                variant="outline"
                loading={isRetrying}
                loadingText="Retrying..."
                onClick={() => onRetry(message.uuid!)}
              >
                <RefreshCw className="mr-1 h-3 w-3" /> Retry send
              </LoadingButton>
            </div>
          )}

          {(isPending || isDraft) && !editing && (
            <div className="flex flex-wrap gap-2 pt-1">
              {isPending && (
                <>
                  <Button size="sm" onClick={() => onApprove(message.uuid!)}>
                    <Check className="mr-1 h-3 w-3" /> Approve & Send
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onReject(message.uuid!)}>
                    <X className="mr-1 h-3 w-3" /> Reject
                  </Button>
                </>
              )}
              {isDraft && isOutbound && (
                <Button size="sm" onClick={() => onSend(message.uuid!)}>
                  <Send className="mr-1 h-3 w-3" /> Send
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditText(message.body_text || '')
                  setEditing(true)
                }}
              >
                <Edit2 className="mr-1 h-3 w-3" /> Edit
              </Button>
            </div>
          )}

          <MessageActionItems links={actionLinks} />

          {(aiAnalysis || canForward) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {aiAnalysis && (
                <button
                  type="button"
                  onClick={() => setShowAnalysis(!showAnalysis)}
                  aria-expanded={showAnalysis}
                  className="flex items-center gap-1.5 text-xs font-medium text-violet-700 hover:text-violet-900 dark:text-violet-300"
                >
                  <Bot className="h-3 w-3" />
                  AI Analysis
                  {showAnalysis ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </button>
              )}
              {canForward && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    onClick={() => setForwardOpen(true)}
                    title="Forward this email to another recipient"
                  >
                    <Forward className="mr-1 h-3 w-3" /> Forward
                  </Button>
                  <ForwardMessageDialog
                    message={message}
                    open={forwardOpen}
                    onOpenChange={setForwardOpen}
                  />
                </>
              )}
            </div>
          )}
          {aiAnalysis && showAnalysis && (
            <div className="rounded-md bg-violet-50 px-3 py-2 dark:bg-violet-950/30">
              <div className="whitespace-pre-wrap text-xs text-violet-950 dark:text-violet-100">
                {aiAnalysis.body_text}
              </div>
            </div>
          )}
        </div>
      )}

      <AnalysisSweep analysis={analysis} />
    </article>
  )
}
